/// <reference types="@angular/localize" />



import { environment } from 'environments/environment';
import { enableProdMode, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApiResult } from 'app/core/models/api-result';
import { ConfigAuthOutputDto } from '@shared/api/model/config-auth-output-dto';
import { AppFacade } from './app/app.facade';
import { provideRouter, ROUTER_CONFIGURATION, RouteReuseStrategy } from '@angular/router';
import { AppRouterReuseStrategy } from './app/app-router-reuse-strategy';
import { AppErrorHandlerInterceptor } from './app/app-error-handler.interceptor';
import { providePrimeNG } from 'primeng/config';
import { appPreset } from './app/app.theme.preset';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app/app-routing';
import { AppComponent } from './app/app.component';
import * as fromApp from './app/app.reducer';
import * as fromAuth from './app/auth/state/auth.reducer';
import * as fromCore from './app/core/state/core.reducer';
import { BrowserUtils } from '@azure/msal-browser';
import { AuthEffects } from 'app/auth/state/auth.effects';
import { AuthFacade } from 'app/auth/auth.facade';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalService } from '@azure/msal-angular';
import { MsalGuardConfigurationFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from 'app/auth/configs/auth.config';
import { DynamicDialogUtils } from '@shared/utils/dynamic-dialog-utils';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationDialogUtils } from '@shared/utils/confirmation-dialog-utils';
import { CoreFacade } from 'app/core/core.facade';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CoreEffects } from 'app/core/state/core.effects';
import { ValueStringByTypePipe } from '@shared/pipes/value-string-by-type.pipe';

const authConfigApiUrl = environment.gatewayApiUrl + "/api/di/v1/auth/config";

export let authConfig: ConfigAuthOutputDto;

if (environment.production) {
    enableProdMode();
}

const span = document.createElement('span');
span.className = 'pi pi-spin pi-spinner text-6xl';

const card = document.createElement('div');
card.append(span);

const container = document.createElement('div');
container.className = 'flex justify-center items-center w-full h-full';

container.append(card);

const appRoot = document.getElementsByTagName('app-root')[0];
appRoot.prepend(container);

fetch(`${authConfigApiUrl}`)
    .then(async (res: Response) => {
        const body = (await res.json()) as ApiResult<ConfigAuthOutputDto>;
        authConfig = body.data!;
        appRoot.removeChild(container);

        bootstrapApplication(AppComponent, {
            providers: [
                provideZoneChangeDetection(),
                
                ...getRouterProviders(),

                ...getStoreProviders(),

                ...getHttpProviders(),

                ...getThemeProviders(),

                ...getFacadeProviders(),
                ...getPipeProviders(),
                ...getDialogProviders(),

                MessageService,

                ...getMsalProviders(),
            ],
            
        })
            .catch(err => console.error(err));
    })
    .catch(() => {
        card.className = 'p-card py-12 px-12';
        span.className = 'text-2xl';
        span.textContent = "An error occured while setting up the app.";
    });

/// PROVIDERS FUNCTIONS

const getHttpProviders = () => {
    return [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppErrorHandlerInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
    ];
}

const getThemeProviders = () => {
    return [
        providePrimeNG({
            theme: {
                preset: appPreset,
                options: {
                    darkModeSelector: 'none',
                    cssLayer: {
                        name: 'primeng',
                        order: 'theme, base, primeng'
                    }
                }
            }
        }),
    ];
};

const getRouterProviders = () => {
    return [
        provideRouter(routes),

        {
            provide: ROUTER_CONFIGURATION,
            useValue:
            {
                // Don't perform initial navigation in iframes or popups

                onSameUrlNavigation: 'reload',
                // Set to enabledBlocking to use Angular Universal
                initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled',
                // canceledNavigationResolution: "replace",
                // paramsInheritanceStrategy: "emptyOnly",
                // urlUpdateStrategy: "deferred",

            }
        },
        {
            provide: RouteReuseStrategy,
            useClass: AppRouterReuseStrategy
        },
    ];
};

const getDialogProviders = () => {
    return [
        DynamicDialogUtils,
        DialogService,
        ConfirmationDialogUtils,
        ConfirmationService,
    ];
};

const getMsalProviders = () => {
    return [
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MsalGuardConfigurationFactory,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalBroadcastService,
        MsalGuard,
    ];
};

const getPipeProviders = () => {
    return [
        ValueStringByTypePipe,
    ];
};

const getFacadeProviders = () => {
    return [
        AppFacade,
        AuthFacade,
        CoreFacade,
    ];
};

const getStoreProviders = () => {
    return [
        provideStore({}, {
            metaReducers: [],
            runtimeChecks: {
                strictActionImmutability: true,
                strictStateImmutability: true
            }
        }),

        provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
        provideEffects([AuthEffects, CoreEffects]),
        provideState(fromApp.APP_FEATURE_KEY, fromApp.appReducer),
        provideState(fromAuth.AUTH_FEATURE_KEY, fromAuth.authReducer),
        provideState(fromCore.CORE_FEATURE_KEY, fromCore.coreReducer),
    ];
};